import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("mv_admin_token")}` },
});

/**
 * Central hook for admin data fetching.
 * Replaces per-page load/useState/useEffect boilerplate.
 *
 * @param {string} endpoint - e.g. "shipments", "pickups", "clients"
 * @param {string[]} extraEndpoints - e.g. ["clients", "drivers"]
 * @returns {{ data, extras, loading, reload }}
 */
export function useAdminData(endpoint, extraEndpoints = []) {
  const [data, setData] = useState([]);
  const [extras, setExtras] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const h = authHeaders();
      const promises = [
        axios.get(`${API}/admin/${endpoint}`, h),
        ...extraEndpoints.map((ep) => axios.get(`${API}/admin/${ep}`, h)),
      ];
      const [main, ...rest] = await Promise.all(promises);
      setData(main.data);
      const ext = {};
      extraEndpoints.forEach((ep, i) => { ext[ep] = rest[i].data; });
      setExtras(ext);
    } catch {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  }, [endpoint, ...extraEndpoints]);

  useEffect(() => { load(); }, [load]);

  return { data, setData, extras, loading, reload: load };
}

/**
 * Hook for admin CRUD operations (create, update, delete).
 * @param {string} endpoint - e.g. "shipments", "clients"
 */
export function useAdminCrud(endpoint) {
  const create = async (payload) => {
    const res = await axios.post(`${API}/admin/${endpoint}`, payload, authHeaders());
    return res.data;
  };

  const update = async (id, payload) => {
    const res = await axios.put(`${API}/admin/${endpoint}/${id}`, payload, authHeaders());
    return res.data;
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/${endpoint}/${id}`, authHeaders());
  };

  return { create, update, remove };
}

/**
 * Hook for admin status updates.
 * @param {string} endpoint - e.g. "shipments", "pickups"
 */
export function useAdminStatus(endpoint) {
  const [showStatus, setShowStatus] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", location: "", note: "" });

  const openStatus = (item) => {
    setShowStatus(item);
    setStatusForm({ status: item.status || "", location: "", note: "" });
  };

  const updateStatus = async (id, onSuccess) => {
    if (!statusForm.status) { toast.error("Seleziona uno stato"); return; }
    try {
      await axios.post(`${API}/admin/${endpoint}/${id}/status`, statusForm, authHeaders());
      toast.success("Stato aggiornato");
      setShowStatus(null);
      if (onSuccess) onSuccess();
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  return { showStatus, setShowStatus, statusForm, setStatusForm, openStatus, updateStatus };
}

/**
 * Hook for assigning drivers.
 * @param {string} endpoint - e.g. "shipments", "pickups"
 */
export function useDriverAssign(endpoint) {
  const assign = async (itemId, driverId, onSuccess) => {
    try {
      const res = await axios.post(`${API}/admin/${endpoint}/${itemId}/assign`, { driver_id: driverId }, authHeaders());
      toast.success(`Assegnato a ${res.data.driver_name}`);
      if (onSuccess) onSuccess();
    } catch {
      toast.error("Errore nell'assegnazione");
    }
  };

  return { assign };
}
